import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Eye, Globe, MousePointerClick, FileText, Info, Building2, UserCheck, Target, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { renderMarkdownContent } from '../../utils/insightsRenderers';

interface InboundInsightsModalProps {
  lead: any;
  onClose: () => void;
}

interface InboundSections {
  source: string;
  action: string;
  content: string;
  additionalDetails: string;
}

interface StandardSections {
  decisionMaker: string;
  company: string;
  outreachAngles: string;
  personalizationHooks: string;
}

function cleanSection(text: string): string {
  return text
    .replace(/^[\s:/]+/, '')
    .replace(/[\s/]+$/, '')
    .replace(/^\([^)]*\)\s*/, '')
    .trim();
}

function parseInboundInsightsSections(raw: string): InboundSections {
  const result: InboundSections = { source: '', action: '', content: '', additionalDetails: '' };
  if (!raw) return result;

  const cleaned = raw.trim();

  const headerRegex = /\b(ADDITIONAL DETAILS|SOURCE|ACTION|CONTENT)\b/g;
  const positions: { key: string; index: number; end: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = headerRegex.exec(cleaned)) !== null) {
    positions.push({ key: match[1].toLowerCase(), index: match.index, end: headerRegex.lastIndex });
  }

  if (positions.length === 0) {
    result.additionalDetails = cleaned;
    return result;
  }

  for (let i = 0; i < positions.length; i++) {
    const start = positions[i].end;
    const end = i + 1 < positions.length ? positions[i + 1].index : cleaned.length;
    const sectionContent = cleanSection(cleaned.substring(start, end));
    assignInbound(result, positions[i].key, sectionContent);
  }

  return result;
}

function assignInbound(result: InboundSections, key: string, content: string) {
  if (key.includes('additional')) {
    result.additionalDetails = content;
  } else if (key === 'source') {
    result.source = content;
  } else if (key === 'action') {
    result.action = content;
  } else if (key === 'content') {
    result.content = content;
  }
}

function parseStandardInsightsSections(raw: string): StandardSections {
  const result: StandardSections = { decisionMaker: '', company: '', outreachAngles: '', personalizationHooks: '' };
  if (!raw) return result;

  const cleaned = raw.replace(/\*\*Summary:\*\*[\s\S]*$/, '').trim();

  const headerRegex = /(?:^|\n)\s*#{0,3}\s*\d*[.)]?\s*(DECISION MAKER INSIGHTS|PERSON INSIGHTS|COMPANY INSIGHTS|OUTREACH ANGLES|PERSONALIZATION HOOKS|PERSONAL HOOKS)\s*:?\s*/gi;
  const positions: { key: string; index: number; contentStart: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = headerRegex.exec(cleaned)) !== null) {
    positions.push({ key: match[1].toLowerCase(), index: match.index, contentStart: headerRegex.lastIndex });
  }

  for (let i = 0; i < positions.length; i++) {
    const end = i + 1 < positions.length ? positions[i + 1].index : cleaned.length;
    const content = cleaned.substring(positions[i].contentStart, end).trim();
    const key = positions[i].key;
    if (key.includes('decision maker') || key === 'person insights') {
      result.decisionMaker = content;
    } else if (key.includes('company')) {
      result.company = content;
    } else if (key.includes('outreach')) {
      result.outreachAngles = content;
    } else if (key.includes('personalization') || key === 'personal hooks') {
      result.personalizationHooks = content;
    }
  }

  return result;
}

interface Box {
  title: string;
  icon: React.FC<any>;
  iconColor: string;
  bgAccent: string;
  content: string;
}

const InboundInsightsModal: React.FC<InboundInsightsModalProps> = ({ lead, onClose }) => {
  const [inboundText, setInboundText] = useState<string | null>(null);
  const [standardText, setStandardText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setIsLoading(true);
        const leadId = lead.lead_id || lead.id;

        const { data, error } = await supabase
          .from('leads_tracking')
          .select('lead_insights, lead_insights_inbound')
          .eq('lead_id', leadId)
          .maybeSingle();

        if (error) throw error;
        setInboundText(data?.lead_insights_inbound || null);
        setStandardText(data?.lead_insights || null);
      } catch (err) {
        console.error('Failed to fetch inbound insights data:', err);
        setError('Failed to load insights data');
      } finally {
        setIsLoading(false);
      }
    };

    if (lead.lead_id || lead.id) {
      fetchInsights();
    }
  }, [lead]);

  const inbound = inboundText ? parseInboundInsightsSections(inboundText) : null;
  const standard = standardText ? parseStandardInsightsSections(standardText) : null;

  const inboundBoxes: Box[] = [
    { title: 'Source', icon: Globe, iconColor: 'text-[#1875FF]', bgAccent: 'from-[#1875FF]/5 to-[#1875FF]/10', content: inbound?.source || '' },
    { title: 'Action', icon: MousePointerClick, iconColor: 'text-[#05E0D1]', bgAccent: 'from-[#05E0D1]/5 to-[#05E0D1]/10', content: inbound?.action || '' },
    { title: 'Content', icon: FileText, iconColor: 'text-[#1875FF]', bgAccent: 'from-[#1875FF]/5 to-[#1875FF]/10', content: inbound?.content || '' },
    { title: 'Additional Details', icon: Info, iconColor: 'text-[#05E0D1]', bgAccent: 'from-[#05E0D1]/5 to-[#05E0D1]/10', content: inbound?.additionalDetails || '' },
  ];

  const standardBoxes: Box[] = [
    { title: 'Company Insights', icon: Building2, iconColor: 'text-[#1875FF]', bgAccent: 'from-[#1875FF]/5 to-[#1875FF]/10', content: standard?.company || '' },
    { title: 'Decision Maker Insights', icon: UserCheck, iconColor: 'text-[#05E0D1]', bgAccent: 'from-[#05E0D1]/5 to-[#05E0D1]/10', content: standard?.decisionMaker || '' },
    { title: 'Outreach Angles', icon: Target, iconColor: 'text-[#1875FF]', bgAccent: 'from-[#1875FF]/5 to-[#1875FF]/10', content: standard?.outreachAngles || '' },
    { title: 'Personalization Hooks', icon: Sparkles, iconColor: 'text-[#05E0D1]', bgAccent: 'from-[#05E0D1]/5 to-[#05E0D1]/10', content: standard?.personalizationHooks || '' },
  ];

  const hasAnyData = !!(inboundText || standardText);

  const renderBox = (box: Box) => {
    const Icon = box.icon;
    return (
      <div
        key={box.title}
        className={`bg-gradient-to-br ${box.bgAccent} border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-white/80 border border-gray-100 flex items-center justify-center shadow-sm">
            <Icon size={14} className={box.iconColor} />
          </div>
          <h4 className="text-sm font-bold text-gray-900 font-display">{box.title}</h4>
        </div>
        <div className="max-h-80 overflow-y-auto pr-1 custom-scrollbar">
          {box.content
            ? renderMarkdownContent(box.content)
            : <span className="text-xs text-gray-400 italic">No data yet</span>}
        </div>
      </div>
    );
  };

  const renderSectionLabel = (label: string) => (
    <div className="flex items-center gap-2 mb-3 mt-1">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-200" />
      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-200" />
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Eye className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Inbound Lead Insights</h3>
              <p className="text-xs text-gray-500 font-medium">{lead.dm_full_name ? `${lead.dm_full_name} - ` : ''}{lead.company_name || 'Unknown Company'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-sm font-medium">{error}</div>
            </div>
          ) : !hasAnyData ? (
            <div className="text-center py-12">
              <Eye size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm font-medium">No insights available for this lead yet</p>
              <p className="text-gray-300 text-xs mt-1">Insights will appear once data is synced from your inbound source</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                {renderSectionLabel('Inbound Insights')}
                <div className="grid grid-cols-2 gap-4">
                  {inboundBoxes.map(renderBox)}
                </div>
              </div>
              <div>
                {renderSectionLabel('Lead Insights')}
                <div className="grid grid-cols-2 gap-4">
                  {standardBoxes.map(renderBox)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InboundInsightsModal;
